// The weekly summary: what was actioned for one firm in one week (Monday to Sunday), so it
// can be printed and given to the firm. Emma asked for it on 21 Oct 2026, for VALS, LACW,
// EAS and RGA.
//
// It is the morning summary's other half. That one says what is waiting; this one says what
// was done. Nothing here is new data: it reads what the tools already keep, and only what
// they keep with a date, so a line is never placed in a week on a guess:
//   - payments recorded on the Fee Slip page (fee_slip_payments), by the day paid;
//   - clerks and instructors emailed, from the line the Fee Slip page writes into the
//     invoice's notes when the email goes ("Clerk emailed 16/10/2026 to ...");
//   - fee slips and invoices that came in by email or with a backsheet;
//   - new file requests that came in, and their status changes (dated from 21 Oct 2026);
//   - fee slip statuses changed on clerk statements (Deadline Guard's audit log);
//   - admin inbox requests lodged and completed, and certifications sent, decided and sent
//     for lodging;
//   - Recover from aid stages moved, LACW aid weeks published, diary weeks saved, and appeal
//     cost certificates and aid applications saved.
//
// Pure, so it is tested without a portal session: the page fetches, this decides.

import { recoupStageLabel, normaliseRecoupData } from "./budgetRecoup.js";
import { escapePrintHtml } from "./clerkStatements.js";
import { emailClientName } from "./emailIntake.js";
import { requestStatusLabel } from "./fileRequests.js";
import { paymentTypeLabel } from "./paymentReport.js";
import { requestTypeLabel } from "./portalRequests.js";
import { readWeekArchive } from "./valsDiaryWeeks.js";

export const WEEKLY_FIRMS = ["vals", "lacw", "eas", "rga"];
export const WEEKLY_FIRM_NAMES = {
  vals: "VALS",
  lacw: "LACW",
  eas: "EAS",
  rga: "Robyn Greensill & Associates",
};
// What a firm calls itself on the Recover from aid stages ("Paid from VALS budget").
const RECOUP_PREFIX = { vals: "VALS", lacw: "LACW", eas: "EAS", rga: "RGA" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CERTIFICATION_DECISIONS = { certified: "Certified", returned: "Returned to the lawyer" };
const asList = (value) => (Array.isArray(value) ? value : []);
const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
const joinParts = (...parts) => parts.map(clean).filter(Boolean).join(" · ");

// ---- Weeks -------------------------------------------------------------------------------

// The Monday a date falls in, at midnight by the local (Melbourne) calendar.
export function mondayOf(date = new Date()) {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() - ((day.getDay() + 6) % 7));
}

export const addWeeks = (monday, weeks) =>
  new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + weeks * 7);

// The week the page opens on: last week, the most recent one that is finished.
export const defaultWeek = (today = new Date()) => addWeeks(mondayOf(today), -1);

// "12–18 Oct 2026", or "28 Sep – 4 Oct 2026" across a month, "29 Dec 2025 – 4 Jan 2026"
// across a year.
export function weekLabel(monday) {
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  const end = `${sunday.getDate()} ${MONTHS[sunday.getMonth()]} ${sunday.getFullYear()}`;
  if (monday.getFullYear() !== sunday.getFullYear()) {
    return `${monday.getDate()} ${MONTHS[monday.getMonth()]} ${monday.getFullYear()} – ${end}`;
  }
  if (monday.getMonth() !== sunday.getMonth()) return `${monday.getDate()} ${MONTHS[monday.getMonth()]} – ${end}`;
  return `${monday.getDate()}–${end}`;
}

// A stored date as a moment. A bare day ("2026-10-16", "16/10/2026") is that day by the
// local calendar, not midnight in London, so a payment dated Monday never lands on Sunday.
export function parseWhen(value) {
  const text = clean(value);
  if (!text) return null;
  let match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (match) return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
  const date = new Date(text);
  return Number.isFinite(date.getTime()) ? date : null;
}

function inWeek(value, monday) {
  const when = parseWhen(value);
  return Boolean(when) && when >= monday && when < addWeeks(monday, 1);
}

// "Mon 12 Oct", for a line's date column.
export function dayLabel(value) {
  const when = parseWhen(value);
  if (!when) return "";
  return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][when.getDay()]} ${when.getDate()} ${MONTHS[when.getMonth()]}`;
}

export function money(value) {
  const amount = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

const amountOf = (value) => {
  const amount = Number.parseFloat(String(value ?? "").replace(/[$,\s]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const line = (when, text, detail = "", extra = {}) => ({ when: parseWhen(when)?.toISOString() || "", day: dayLabel(when), text: clean(text), detail: clean(detail), ...extra });
const byWhen = (a, b) => a.when.localeCompare(b.when) || a.text.localeCompare(b.text);

// ---- Sections ----------------------------------------------------------------------------

// Payments recorded on the Fee Slip page, in the week they were paid.
export function paymentLines(payments = [], monday) {
  return asList(payments)
    .filter((payment) => !payment?.voided_at && inWeek(payment?.paid_on || payment?.created_at, monday))
    .map((payment) => line(
      payment.paid_on || payment.created_at,
      payment.client_name || "Unnamed client",
      joinParts(paymentTypeLabel(payment.payment_type), payment.clerk_name, payment.invoice_ref),
      { amount: amountOf(payment.amount_paid) },
    ))
    .sort(byWhen);
}

// "Clerk emailed 16/10/2026 to holmesaccounts@vicbar.com.au." - the line the Fee Slip page
// adds to an invoice's notes when its email goes (afterEmailSent, src/lib/clerkEmail.js).
const EMAILED = /\b(Clerk|Instructor) emailed (\d{1,2}\/\d{1,2}\/\d{4})(?: to (.+?))?\.(?=\s|$)/gi;

export function emailedNotes(notes = "") {
  return [...String(notes || "").matchAll(EMAILED)].map((match) => ({
    who: match[1].toLowerCase() === "clerk" ? "Clerk" : "Instructor",
    date: match[2],
    to: clean(match[3]),
  }));
}

// Every invoice row the Fee Slip page shows, from wherever it came, in one shape: the
// emailed lines can be on any of them.
function feeSlipRows({ statementRows = [], intake = [], backsheets = [] } = {}) {
  return [
    ...asList(statementRows).map((row) => ({
      key: row.id, clientName: row.clientName, clerkName: row.clerkName, invoice: row.invoiceName, notes: row.notes,
    })),
    ...asList(intake).map((item) => ({
      key: `email-${item.id}`, clientName: emailClientName(item), clerkName: item.clerk_name, invoice: item.invoice_ref || item.file_name, notes: item.notes,
    })),
    ...asList(backsheets).filter((draft) => draft?.form_data?.counsel_invoice).map((draft) => {
      const form = draft.form_data;
      return {
        key: `invoice-${draft.id}`,
        clientName: clean(`${form.client_first_name || ""} ${form.client_surname || ""}`),
        clerkName: form.clerk,
        invoice: form.counsel_invoice.name,
        notes: form.counsel_invoice.payment_notes,
      };
    }),
  ];
}

export function emailedLines(sources = {}, monday) {
  const seen = new Set();
  const lines = [];
  for (const row of feeSlipRows(sources)) {
    for (const sent of emailedNotes(row.notes)) {
      if (!inWeek(sent.date, monday)) continue;
      const key = `${row.key}|${sent.who}|${sent.date}|${sent.to}`;
      if (seen.has(key)) continue;
      seen.add(key);
      lines.push(line(
        sent.date,
        `${sent.who} emailed: ${clean(row.clientName) || "Unnamed client"}`,
        joinParts(row.clerkName, row.invoice, sent.to && `to ${sent.to}`),
      ));
    }
  }
  return lines.sort(byWhen);
}

// What an emailed item is called. Most still have their type to set ("check type" on the Fee
// Slip page), which means nothing to a firm, so those are simply invoices.
const EMAILED_KINDS = { fee_slip: "Fee slip by email", disbursement: "Disbursement by email" };

// Fee slips and invoices that arrived: filed from email, or uploaded with a backsheet.
export function receivedLines({ intake = [], backsheets = [] } = {}, monday) {
  const fromEmail = asList(intake)
    .filter((item) => item?.kind !== "request" && inWeek(item?.received_at || item?.created_at, monday))
    .map((item) => line(
      item.received_at || item.created_at,
      emailClientName(item),
      joinParts(EMAILED_KINDS[item.kind] || "Invoice by email", item.clerk_name, item.amount ? money(item.amount) : ""),
    ));
  const withBacksheet = asList(backsheets)
    .filter((draft) => draft?.form_data?.counsel_invoice && inWeek(draft.form_data.counsel_invoice.uploaded_at || draft.created_at, monday))
    .map((draft) => {
      const form = draft.form_data;
      return line(
        form.counsel_invoice.uploaded_at || draft.created_at,
        clean(`${form.client_first_name || ""} ${form.client_surname || ""}`) || "Unnamed client",
        joinParts("Counsel invoice with backsheet", form.counsel_name, form.counsel_invoice.amount ? money(form.counsel_invoice.amount) : ""),
      );
    });
  return [...fromEmail, ...withBacksheet].sort(byWhen);
}

// Status changes on new file requests are dated from this day (Deadline Guard stamps them);
// a week before it can only show requests coming in.
export const FILE_REQUEST_STATUS_DATES_FROM = new Date(2026, 9, 21);

// New file requests that came in this week, with where each stands now, and those whose
// status changed this week. Only the latest change of each is kept, so a request moved twice
// shows where it ended up.
export function fileRequestLines(requests = [], monday) {
  const lines = [];
  for (const request of asList(requests)) {
    const client = request?.clientName || "Unknown client";
    if (inWeek(request?.date, monday)) {
      lines.push(line(request.date, `New file request: ${client}`, joinParts(request.category, `now ${requestStatusLabel(request.status)}`)));
    }
    if (inWeek(request?.statusUpdatedAt, monday)) {
      lines.push(line(request.statusUpdatedAt, `${requestStatusLabel(request.status)}: ${client}`, joinParts(request.category, `came in ${dayLabel(request.date)}`)));
    }
  }
  return lines.sort(byWhen);
}

// Statuses already reported as clerks and instructors emailed.
const EMAILED_STATUSES = new Set(["clerk emailed", "instructor emailed"]);

// Status changes on clerk statement rows (GET /api/clerk-statements/activity), for one firm.
export function statementStatusLines(changes = [], monday) {
  return asList(changes)
    .filter((change) => !EMAILED_STATUSES.has(clean(change?.to_status).toLowerCase()) && inWeek(change?.at, monday))
    .map((change) => line(
      change.at,
      `${clean(change.to_status) || "Status cleared"}: ${change.client_name || "Unnamed client"}`,
      joinParts(change.clerk_name, change.invoice_number, `was ${clean(change.from_status) || "no status"}`),
    ))
    .sort(byWhen);
}

// Admin inbox requests the firm lodged, and those completed or cancelled.
export function requestLines(requests = [], monday) {
  const lines = [];
  for (const request of asList(requests)) {
    const what = joinParts(requestTypeLabel(request?.request_type), request?.client_name || request?.title);
    if (inWeek(request?.submitted_at || request?.created_at, monday)) {
      lines.push(line(request.submitted_at || request.created_at, `Request lodged: ${what}`, request.submitted_by ? `by ${request.submitted_by}` : ""));
    }
    if (["completed", "cancelled"].includes(request?.status) && inWeek(request?.actioned_at, monday)) {
      lines.push(line(
        request.actioned_at,
        `Request ${request.status === "completed" ? "completed" : "cancelled"}: ${what}`,
        request.action_note,
      ));
    }
  }
  return lines.sort(byWhen);
}

export function certificationLines(rows = [], monday) {
  const lines = [];
  for (const row of asList(rows)) {
    const client = joinParts(row?.client_name || "Legal aid application", row?.file_number);
    if (inWeek(row?.submitted_at, monday)) {
      lines.push(line(row.submitted_at, `Sent for certification: ${client}`, row.submitted_by || row.submitted_by_email ? `by ${row.submitted_by || row.submitted_by_email}` : ""));
    }
    if (CERTIFICATION_DECISIONS[row?.status] && inWeek(row?.decided_at, monday)) {
      lines.push(line(
        row.decided_at,
        `${CERTIFICATION_DECISIONS[row.status]}: ${client}`,
        joinParts(row.decided_by || row.decided_by_email ? `by ${row.decided_by || row.decided_by_email}` : "", row.decision_note),
      ));
    }
    if (inWeek(row?.lodge_requested_at, monday)) {
      lines.push(line(row.lodge_requested_at, `Sent to be lodged: ${client}`, row.lodge_requested_by ? `by ${row.lodge_requested_by}` : ""));
    }
  }
  return lines.sort(byWhen);
}

// Recover from aid: invoices whose stage was set this week. Only each invoice's latest stage
// is kept, so an invoice moved twice in a week shows where it ended up.
export function recoupLines(formData, monday, brand) {
  const data = normaliseRecoupData(formData);
  const prefix = RECOUP_PREFIX[brand] || "";
  const lines = [];
  for (const entry of Object.values(data.entries)) {
    if (!inWeek(entry.updatedAt, monday)) continue;
    lines.push(line(entry.updatedAt, `${recoupStageLabel(entry.stage, prefix)}: ${entry.clientName || "Unnamed client"}`, joinParts(entry.payee, entry.invoiceRef), { amount: amountOf(entry.amount) }));
  }
  for (const invoice of data.invoices) {
    if (inWeek(invoice.stageAt, monday)) {
      lines.push(line(invoice.stageAt, `${recoupStageLabel(invoice.stage, prefix)}: ${invoice.clientName || "Unnamed client"}`, joinParts(invoice.payee, invoice.invoiceRef), { amount: amountOf(invoice.amount) }));
    } else if (inWeek(invoice.uploadedAt, monday)) {
      lines.push(line(invoice.uploadedAt, `Added to the tracker: ${invoice.clientName || "Unnamed client"}`, joinParts(recoupStageLabel(invoice.stage, prefix), invoice.payee, invoice.invoiceRef), { amount: amountOf(invoice.amount) }));
    }
  }
  return lines.sort(byWhen);
}

// LACW's aid status weeks published from the Billing Sheet, and the diary weeks saved.
export function billingLines({ aidWeeks = [], diaryWeeks = [] } = {}, monday) {
  const published = asList(aidWeeks)
    .filter((row) => inWeek(row?.form_data?.publishedAt, monday))
    .map((row) => {
      const rows = asList(row.form_data.rows);
      return line(row.form_data.publishedAt, `Aid status week published: ${row.form_data.weekLabel || row.draft_name || "LACW week"}`, `${rows.length} appearance${rows.length === 1 ? "" : "s"}`);
    });
  const saved = asList(diaryWeeks)
    .filter((row) => inWeek(row?.updated_at || row?.created_at, monday))
    .map((row) => {
      const archive = readWeekArchive(row.form_data);
      const count = archive?.entries?.length || 0;
      return line(row.updated_at || row.created_at, `Diary week saved: ${archive?.weekName || row.draft_name || "Diary week"}`, `${count} appearance${count === 1 ? "" : "s"}`);
    });
  return [...published, ...saved].sort(byWhen);
}

const FORM_NAMES = {
  appeal_cost_certificate: "Appeal cost certificate",
  apply_for_aid: "Apply for Aid",
  legal_aid_form: "Legal aid application",
};
export const SAVED_FORM_TYPES = Object.keys(FORM_NAMES);

// Appeal cost certificates and aid applications saved this week. Only the last save of each
// is kept, so a form saved again later shows in that later week instead.
export function formLines(drafts = [], monday) {
  return asList(drafts)
    .filter((draft) => FORM_NAMES[draft?.form_type] && inWeek(draft.updated_at || draft.created_at, monday))
    .map((draft) => line(draft.updated_at || draft.created_at, `${FORM_NAMES[draft.form_type]} saved: ${draft.draft_name || "Untitled"}`, draft.owner_name ? `by ${draft.owner_name}` : ""))
    .sort(byWhen);
}

// ---- The summary -------------------------------------------------------------------------

// Where each section's work is done in the admin portal.
function pages(brand) {
  return {
    feeSlips: { tab: `${brand}-fee-slip-invoice-payment`, page: "Fee Slips" },
    fileRequests: { tab: `${brand}-emails`, page: "New File Emails" },
    inbox: { tab: "inbox", page: "Admin inbox" },
    certifications: { tab: "certifications", page: "Certifications" },
    billing: brand === "lacw"
      ? { tab: "billing", page: "Billing Sheet" }
      : brand === "vals" || brand === "eas"
        ? { tab: `${brand}-diary`, page: `${WEEKLY_FIRM_NAMES[brand]} Diary` }
        : null,
    forms: { tab: "draft-forms", page: "Draft forms" },
  };
}

const firmPart = (value, brand) => (value && typeof value === "object" && !Array.isArray(value) ? value[brand] : undefined);

// One firm's week. `data` holds each source by firm ({ payments: { vals: [...] }, ... });
// a source missing from it could not be read, and is named rather than shown as empty.
export function buildWeeklySummary(data = {}, { brand, monday }) {
  const to = pages(brand);
  const has = (key) => data[key] !== undefined;
  const of = (key) => firmPart(data[key], brand);
  const sections = [];
  const unavailable = [];
  const add = (id, title, sources, page, build, note = "") => {
    const missing = sources.filter((key) => !has(key));
    if (missing.length === sources.length) {
      unavailable.push(title);
      return;
    }
    if (missing.length) unavailable.push(title);
    const lines = build();
    const total = lines.some((entry) => "amount" in entry) ? lines.reduce((sum, entry) => sum + (entry.amount || 0), 0) : null;
    sections.push({ id, title, ...page, lines, total, note });
  };

  add("payments", "Payments recorded", ["payments"], to.feeSlips, () => paymentLines(of("payments"), monday));
  add("emailed", "Clerks and instructors emailed", ["statementRows", "intake", "backsheets"], to.feeSlips,
    () => emailedLines({ statementRows: of("statementRows"), intake: of("intake"), backsheets: of("backsheets") }, monday));
  add("statuses", "Fee slip statuses changed", ["statementChanges"], to.feeSlips, () => statementStatusLines(of("statementChanges"), monday));
  add("file-requests", "New file requests", ["fileRequests"], to.fileRequests, () => fileRequestLines(of("fileRequests"), monday),
    monday < FILE_REQUEST_STATUS_DATES_FROM
      ? "Status changes weren't dated before 21 Oct 2026, so this week shows the requests that came in and where each stands now."
      : "");
  add("requests", "Admin requests", ["requests"], to.inbox,
    () => requestLines(asList(data.requests).filter((request) => String(request?.brand || "").toLowerCase() === brand), monday));
  add("certifications", "Certifications", ["certifications"], to.certifications, () => certificationLines(of("certifications"), monday));
  add("recoup", "Recover from aid", ["recoup"], to.feeSlips, () => recoupLines(of("recoup"), monday, brand));
  if (to.billing) {
    add("billing", brand === "lacw" ? "Billing and aid weeks" : "Diary weeks", brand === "lacw" ? ["aidWeeks"] : ["diaryWeeks"], to.billing,
      () => billingLines({ aidWeeks: brand === "lacw" ? data.aidWeeks : [], diaryWeeks: of("diaryWeeks") }, monday));
  }
  add("forms", "Forms saved", ["forms"], to.forms, () => formLines(of("forms"), monday));
  // Last: what came in, rather than what was done, and often the longest list.
  add("received", "Fee slips and invoices received", ["intake", "backsheets"], to.feeSlips,
    () => receivedLines({ intake: of("intake"), backsheets: of("backsheets") }, monday));

  return {
    brand,
    firm: WEEKLY_FIRM_NAMES[brand] || String(brand || "").toUpperCase(),
    week: weekLabel(monday),
    sections,
    count: sections.reduce((sum, section) => sum + section.lines.length, 0),
    unavailable,
  };
}

// ---- Printing ----------------------------------------------------------------------------

function summaryHtml(summary) {
  const sections = summary.sections.filter((section) => section.lines.length);
  const body = sections.map((section) => {
    const withAmounts = section.total !== null;
    return `
      <h2>${escapePrintHtml(section.title)} <span>${section.lines.length}${withAmounts ? ` &middot; ${escapePrintHtml(money(section.total))}` : ""}</span></h2>
      ${section.note ? `<p class="note">${escapePrintHtml(section.note)}</p>` : ""}
      <table><tbody>${section.lines.map((entry) => `<tr>
        <td class="day">${escapePrintHtml(entry.day)}</td>
        <td>${escapePrintHtml(entry.text)}${entry.detail ? `<div class="detail">${escapePrintHtml(entry.detail)}</div>` : ""}</td>
        ${withAmounts ? `<td class="r">${escapePrintHtml(money(entry.amount))}</td>` : ""}
      </tr>`).join("")}</tbody></table>`;
  }).join("");
  return `<section class="firm">
    <h1>${escapePrintHtml(summary.firm)} &mdash; work actioned, ${escapePrintHtml(summary.week)}</h1>
    <div class="sub">Complete Law Support &middot; ${summary.count} item${summary.count === 1 ? "" : "s"}</div>
    ${body || "<p>Nothing was recorded as actioned this week.</p>"}
    ${summary.unavailable.length ? `<p class="note">Not included (could not be read): ${escapePrintHtml(summary.unavailable.join(", "))}.</p>` : ""}
  </section>`;
}

// A printable page for one firm or several (each on its own page), printed on open.
export function weeklySummaryPrintHtml(summaries = [], printedAt = new Date()) {
  const list = asList(summaries);
  const title = list.length === 1 ? `${list[0].firm} weekly summary ${list[0].week}` : `Weekly summary ${list[0]?.week || ""}`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapePrintHtml(title)}</title><style>
    body{font-family:Arial,sans-serif;font-size:11px;color:#0f172a;margin:14mm}
    h1{font-size:16px;margin:0 0 2px}h2{font-size:12px;margin:16px 0 4px}h2 span{font-weight:normal;color:#475569}
    .sub,.printed{color:#475569;margin-bottom:6px}.note{color:#475569;font-style:italic;margin:2px 0 4px}
    table{width:100%;border-collapse:collapse}td{border-top:1px solid #e2e8f0;padding:4px 6px;text-align:left;vertical-align:top}
    td.day{width:70px;white-space:nowrap;color:#475569}.detail{color:#64748b;font-size:10px}.r{text-align:right;white-space:nowrap}
    .firm+.firm{page-break-before:always}
  </style></head><body>
    ${list.map(summaryHtml).join("")}
    <div class="printed">Printed ${escapePrintHtml(printedAt.toLocaleString("en-AU"))}</div>
    <script>window.addEventListener("load",()=>window.print());<\/script>
  </body></html>`;
}
